import { Plan } from '@/src/components/desktop/srp/fareFamily/types'
import type { Fare, FlightCard } from '@/src/types/flight'
import { getFormattedDate } from '../date'
import { isEmpty } from '../generalUtils'
import { constructSplrt } from '../srp/RT/constructSplRT'

// Helper function to check if a plan is regular fare
function isRegularFare(plan: Plan | null): boolean {
    return !plan || plan.ctBrandName === 'Regular'
}

// Helper function to build fare object from Plan
function buildFareFromPlan(
    plan: Plan,
    dfd: string
): {
    basePrice: number
    fareKey: string
    price: number
    fareType: string
    solutionId: string
    comboFBC: string
    fareClass: string
} {
    return {
        basePrice: plan.basePrice,
        fareKey: plan.fareKey,
        price: plan.unModifiedTotalPrice || plan.price, //for RT journey unModifiedTotalPrice is used , for OW journey unModifiedTotalPrice is undefined
        fareType: dfd === 'R' ? 'REFUNDABLE' : 'NON_REFUNDABLE',
        solutionId: plan.solutionId,
        comboFBC: plan.comboFbc,
        fareClass: 'RETAIL'
    }
}

// Helper function to build fare object from Fare
function buildFareFromFlight(
    fareObj: Fare,
    dfd: string,
    solutionId: string | undefined,
    fareClass: string
): {
    basePrice: number
    fareKey: string
    price: number
    fareType: string
    solutionId: string | undefined
    comboFBC: string
    fareClass: string
} {
    return {
        basePrice: fareObj.bp,
        fareKey: fareObj.fk,
        price: fareObj.pr,
        fareType: dfd === 'R' ? 'REFUNDABLE' : 'NON_REFUNDABLE',
        solutionId: solutionId,
        comboFBC: fareObj.fb,
        fareClass: fareClass
    }
}

export function buildItineraryPayload({
    triggerSrpEvent,
    urlQueryObj,
    flight,
    jsons,
    utmCurrency = 'SAR',
    fareFamilyData
}: {
    triggerSrpEvent?: (event: string, data: Record<string, string | number>) => void
    urlQueryObj: Record<string, string>
    flight: FlightCard[]
    jsons: any
    utmCurrency: string
    fareFamilyData?: (Plan | null)[]
}) {
    const onwardFlight = flight[0]
    const returnFlight = flight[1]
    const isReturnTrip = !!returnFlight && !!urlQueryObj.return_date

    // Determine if we should use constructSplrt logic (only for RT with both regular fares)
    let shouldUseSplrt = false
    if (isReturnTrip) {
        const onwardPlan = fareFamilyData?.[0] ?? null
        const returnPlan = fareFamilyData?.[1] ?? null
        const bothAreRegular = isRegularFare(onwardPlan) && isRegularFare(returnPlan)
        shouldUseSplrt = bothAreRegular
    }

    // Calculate SPLRT fares if needed
    let isSplrtRt = false
    let onwardSplrtFare, returnSplrtFare
    let onwardDfd, returnDfd
    let fareClass = 'RETAIL'
    let onwardSolutionId, returnSolutionId
    let onwardFareOBJ: Fare, returnFareOBJ: Fare

    if (shouldUseSplrt) {
        onwardSplrtFare = constructSplrt(onwardFlight, returnFlight?.splRtFn)
        returnSplrtFare = constructSplrt(returnFlight, onwardFlight?.splRtFn)

        isSplrtRt = !!(onwardSplrtFare?.isSpecialRT && returnSplrtFare?.isSpecialRT)
        if (isSplrtRt) {
            const totalRetailPrice =
                (onwardFlight?.priceBreakup?.fare?.pr || 0) + (returnFlight?.priceBreakup?.fare?.pr || 0)
            const totalSplrtRtPrice = (onwardSplrtFare?.sFare?.pr || 0) + (returnSplrtFare?.sFare?.pr || 0)
            isSplrtRt = totalSplrtRtPrice > 0 && totalSplrtRtPrice <= totalRetailPrice
        }

        onwardDfd = isSplrtRt ? onwardSplrtFare?.splrtDfd : onwardFlight?.dfd
        returnDfd = isSplrtRt ? returnSplrtFare?.splrtDfd : returnFlight?.dfd
        fareClass = isSplrtRt ? 'SPECIAL_RT' : 'RETAIL'
        onwardSolutionId = isSplrtRt ? onwardSplrtFare?.solutionId : onwardFlight?.priceBreakup?.fare?.solutionId
        returnSolutionId = isSplrtRt ? returnSplrtFare?.solutionId : returnFlight?.priceBreakup?.fare?.solutionId

        onwardFareOBJ =
            isSplrtRt && !isEmpty(onwardSplrtFare?.sFare)
                ? (onwardSplrtFare?.sFare as Fare)
                : onwardFlight?.priceBreakup?.fare || {}
        returnFareOBJ =
            isSplrtRt && !isEmpty(returnSplrtFare?.sFare)
                ? (returnSplrtFare?.sFare as Fare)
                : returnFlight?.priceBreakup?.fare || {}
    } else {
        // For non-SPLRT cases, use regular flight data or fare family data
        onwardDfd = onwardFlight?.dfd
        returnDfd = returnFlight?.dfd
        onwardSolutionId = onwardFlight?.priceBreakup?.fare?.solutionId
        returnSolutionId = returnFlight?.priceBreakup?.fare?.solutionId
        onwardFareOBJ = onwardFlight?.priceBreakup?.fare || {}
        returnFareOBJ = returnFlight?.priceBreakup?.fare || {}
    }

    // Build onward fare
    const onwardPlan = fareFamilyData?.[0] ?? null
    let onwardFare
    if (!isReturnTrip && onwardPlan) {
        // One-way flight: use fare family data if available
        onwardFare = buildFareFromPlan(onwardPlan, onwardDfd || '')
    } else if (isReturnTrip && onwardPlan && !isRegularFare(onwardPlan)) {
        // Return trip: use fare family data only if not regular
        onwardFare = buildFareFromPlan(onwardPlan, onwardDfd || '')
    } else if (shouldUseSplrt) {
        // Use SPLRT logic
        onwardFare = buildFareFromFlight(onwardFareOBJ, onwardDfd || '', onwardSolutionId, fareClass)
    } else {
        // Use regular flight data
        onwardFare = buildFareFromFlight(onwardFareOBJ, onwardDfd || '', onwardSolutionId, fareClass)
    }

    // Build return fare
    let returnFare = null
    if (returnFlight) {
        const returnPlan = fareFamilyData?.[1] ?? null
        if (returnPlan && !isRegularFare(returnPlan)) {
            // Use fare family data
            returnFare = buildFareFromPlan(returnPlan, returnDfd || '')
        } else if (shouldUseSplrt) {
            // Use SPLRT logic
            returnFare = buildFareFromFlight(returnFareOBJ, returnDfd || '', returnSolutionId, fareClass)
        } else {
            // Use regular flight data
            returnFare = buildFareFromFlight(returnFareOBJ, returnDfd || '', returnSolutionId, fareClass)
        }
    }

    // Set fareGroupDisplayName if fare family data is available
    const itineraryMeta: any = {
        domain: jsons?.domain,
        ...(jsons?.userInfo && { userId: jsons.userInfo.usrId }),
        externalApi: false,
        international: true,
        multiCity: false,
        sid: jsons?.psid || '',
        abFeatures: ['FARE_BENEFIT_INFO'],
        sourceType: 'B2C',
        utmCurrency: utmCurrency,
        convFeeRequired: false,
        couponCode: urlQueryObj.couponCode || '',
        trackingDetails: { selectedCardListId: [0] },
        dcFlow: true,
        flowType: 'Flyin'
    }

    // Add fareGroupDisplayName if we have fare family data
    if (!isReturnTrip && onwardPlan) {
        // One-way: use title if plan exists
        itineraryMeta.fareGroupDisplayName = onwardPlan.title
    } else if (isReturnTrip && onwardPlan && !isRegularFare(onwardPlan)) {
        // Return trip: use onward plan title if not regular
        itineraryMeta.fareGroupDisplayName = onwardPlan.title
    } else if (isReturnTrip && returnFlight && fareFamilyData?.[1] && !isRegularFare(fareFamilyData[1])) {
        // Return trip: use return plan title if not regular
        itineraryMeta.fareGroupDisplayName = fareFamilyData[1].title
    }

    //add event for RT
    try {
        if (isReturnTrip) {
            triggerSrpEvent?.('Air_UI_Action', {
                action_name: 'RT-initiate-create-itinerary',
                action_type: 'Click',
                onward_fare_key: onwardFare?.fareKey,
                return_fare_key: returnFare?.fareKey || '',
                onward_fare_price: onwardFare?.price || 0,
                return_fare_price: returnFare?.price || 0,
                fare_class: fareClass,
                journey_type: 'Return',
                onward_fare_rule_type: isRegularFare(onwardPlan) ? 'Regular' : onwardPlan?.ctBrandName || '',
                return_fare_rule_type: isRegularFare(fareFamilyData?.[1] || null)
                    ? 'Regular'
                    : (fareFamilyData?.[1] || null)?.ctBrandName || ''
            })
        }
    } catch (error) {
        console.error('Error triggerSrpEvent: RT-initiate-create-itinerary', error)
    }
    return {
        flightParams: [
            {
                cabinType: urlQueryObj.class?.toUpperCase() || 'ECONOMY',
                departDate: urlQueryObj.depart_date ? getFormattedDate(urlQueryObj.depart_date, 'YYYY-MM-DD') : '',
                from: urlQueryObj.from || '',
                returnDate: urlQueryObj.return_date ? getFormattedDate(urlQueryObj.return_date, 'YYYY-MM-DD') : '',
                onwardFares: [onwardFare],
                returnFares: returnFare ? [returnFare] : [],
                to: urlQueryObj.to || ''
            }
        ],
        itineraryMeta,
        paxInfo: {
            adults: Number(urlQueryObj.adults || 1),
            children: Number(urlQueryObj.childs || 0),
            infants: Number(urlQueryObj.infants || 0)
        }
    }
}
